import React from 'react';
import { PnFChart as PnFChartType } from '../../types';

interface PnFChartProps {
  chart: PnFChartType;
  width?: number;
  height?: number;
}

const PnFChart: React.FC<PnFChartProps> = ({ chart, width = 800, height = 400 }) => {
  if (!chart || !chart.columns || chart.columns.length === 0) {
    return <div className="text-gray-500">No chart data available</div>;
  }

  // Calculate price range for scaling
  let minPrice = Number.MAX_VALUE;
  let maxPrice = Number.MIN_VALUE;

  chart.columns.forEach(column => {
    column.boxes.forEach(price => {
      minPrice = Math.min(minPrice, price);
      maxPrice = Math.max(maxPrice, price);
    });
    
    // Also check high and low values
    minPrice = Math.min(minPrice, column.low);
    maxPrice = Math.max(maxPrice, column.high);
  });

  // Handle the case where no valid prices were found
  if (minPrice === Number.MAX_VALUE || maxPrice === Number.MIN_VALUE) {
    minPrice = 0;
    maxPrice = 100;
  }

  // Add padding to the price range
  const padding = (maxPrice - minPrice) * 0.1;
  minPrice -= padding;
  maxPrice += padding;

  // Calculate scales
  const priceRange = maxPrice - minPrice;
  const columnWidth = Math.min(40, (width - 100) / chart.columns.length);

  // Calculate Y position from price
  const getYPosition = (price: number): number => {
    return height - 50 - ((price - minPrice) / priceRange) * (height - 100);
  };

  // Generate price scale ticks
  const priceTicks = [];
  const tickCount = 10;
  for (let i = 0; i <= tickCount; i++) {
    const price = minPrice + (priceRange * i) / tickCount;
    priceTicks.push(price);
  }

  // Detect patterns
  const hasPattern = chart.patterns?.tripleTopBreakout || 
                     chart.patterns?.tripleTopBreakdown || 
                     chart.patterns?.quadTopBreakout || 
                     chart.patterns?.quadTopBreakdown;

  // Get pattern description
  const getPatternDescription = (): string => {
    if (!chart.patterns) return "No Pattern Detected";
    if (chart.patterns.tripleTopBreakout) return "Triple Top Breakout";
    if (chart.patterns.tripleTopBreakdown) return "Triple Top Breakdown";
    if (chart.patterns.quadTopBreakout) return "Quad Top Breakout";
    if (chart.patterns.quadTopBreakdown) return "Quad Top Breakdown";
    return "No Pattern Detected";
  };

  // Default price to show if currentPrice is undefined
  const currentPrice = chart.currentPrice || ((minPrice + maxPrice) / 2);

  return (
    <div className="relative">
      <h3 className="text-lg font-medium text-gray-900 mb-2">{chart.ticker} - Point & Figure Chart</h3>
      
      {hasPattern && chart.patterns && (
        <div className={`mb-2 px-3 py-1 inline-block rounded text-sm font-medium ${
          chart.patterns?.tripleTopBreakout || chart.patterns?.quadTopBreakout
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {getPatternDescription()}
        </div>
      )}
      
      <svg width={width} height={height} className="border border-gray-200 rounded">
        {/* Y axis (price) */}
        <line x1="50" y1="50" x2="50" y2={height - 50} stroke="#CBD5E1" strokeWidth="1" />
        
        {/* Y axis labels */}
        {priceTicks.map((price, i) => (
          <g key={`tick-${i}`}>
            <line 
              x1="45" 
              y1={getYPosition(price)} 
              x2="50" 
              y2={getYPosition(price)} 
              stroke="#94A3B8" 
              strokeWidth="1" 
            />
            <text 
              x="40" 
              y={getYPosition(price)} 
              textAnchor="end" 
              dominantBaseline="middle" 
              fontSize="12" 
              fill="#64748B"
            >
              {price.toFixed(price < 10 ? 2 : price < 100 ? 1 : 0)}
            </text>
          </g>
        ))}
        
        {/* X axis */}
        <line x1="50" y1={height - 50} x2={width - 50} y2={height - 50} stroke="#CBD5E1" strokeWidth="1" />
        
        {/* Chart columns */}
        {chart.columns.map((column, colIndex) => {
          const x = 50 + colIndex * columnWidth + columnWidth / 2;
          
          return (
            <g key={`col-${colIndex}`}>
              {/* Column label */}
              <text 
                x={x} 
                y={height - 35} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#64748B"
              >
                {colIndex + 1}
              </text>
              
              {/* Boxes */}
              {column.boxes.map((price, boxIndex) => (
                <text 
                  key={`box-${colIndex}-${boxIndex}`}
                  x={x}
                  y={getYPosition(price)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="14"
                  fontWeight="bold"
                  fill={column.direction === 'up' ? '#059669' : '#DC2626'}
                >
                  {column.direction === 'up' ? 'X' : 'O'}
                </text>
              ))}
            </g>
          );
        })}
        
        {/* Current price indicator */}
        <line 
          x1="50" 
          y1={getYPosition(currentPrice)} 
          x2={width - 50} 
          y2={getYPosition(currentPrice)} 
          stroke="#3B82F6" 
          strokeWidth="1" 
          strokeDasharray="4 2" 
        />
        <text 
          x={width - 45} 
          y={getYPosition(currentPrice)} 
          textAnchor="end" 
          dominantBaseline="middle" 
          fontSize="12" 
          fill="#3B82F6"
        >
          Current: {currentPrice.toFixed(2)}
        </text>
      </svg>
    </div>
  );
};

export default PnFChart; 